import React from 'react';
import Landing from './pages/Landing.jsx';
import LoginStudent from './pages/LoginStudent.jsx';
import LoginStaff from './pages/LoginStaff.jsx';
import StudentDashboard from './pages/student/Dashboard.jsx';
import StudentStudyPlan from './pages/student/StudyPlan.jsx';
import StudentRequests from './pages/student/Requests.jsx';
import StudentRequestNew from './pages/student/RequestNew.jsx';
import StudentRequestDetail from './pages/student/RequestDetail.jsx';
import StudentSurveys from './pages/student/Surveys.jsx';
import StudentProfile from './pages/student/Profile.jsx';
import StudentForms from './pages/student/Forms.jsx';
import StudentDegree from './pages/student/Degree.jsx';
import StudentContact from './pages/student/Contact.jsx';
import StaffDashboard from './pages/staff/Dashboard.jsx';
import StaffStudents from './pages/staff/Students.jsx';
import StaffStudentNew from './pages/staff/StudentNew.jsx';
import StaffStudentDetail from './pages/staff/StudentDetail.jsx';
import StaffStudentEdit from './pages/staff/StudentEdit.jsx';
import StaffStudentDelete from './pages/staff/StudentDelete.jsx';
import StaffStudentPlanEdit from './pages/staff/StudentPlanEdit.jsx';
import StaffRequests from './pages/staff/Requests.jsx';
import StaffRequestDetail from './pages/staff/RequestDetail.jsx';
import StaffSurveys from './pages/staff/Surveys.jsx';
import StaffSurveyNew from './pages/staff/SurveyNew.jsx';
import StaffSurveyDetail from './pages/staff/SurveyDetail.jsx';
import StaffPages from './pages/staff/Pages.jsx';
import StaffSettings from './pages/staff/Settings.jsx';
import StaffEmails from './pages/staff/Emails.jsx';
import StaffEmailDetail from './pages/staff/EmailDetail.jsx';
import StaffLogs from './pages/staff/Logs.jsx';
import StaffCourseList from './pages/staff/CourseList.jsx';
import StaffAnalytics from './pages/staff/Analytics.jsx';
import StaffAdmins from './pages/staff/Admins.jsx';
import StaffAdminDetail from './pages/staff/AdminDetail.jsx';
import Placeholder from './pages/Placeholder.jsx';
import NotFound from './pages/NotFound.jsx';
import Logout from './pages/Logout.jsx';
import Layout from './components/Layout.jsx';

const routes = [
  { path: /^\/$/, component: Landing, layout: 'public' },
  { path: /^\/login\/student$/, component: LoginStudent, layout: 'public' },
  { path: /^\/login\/staff$/, component: LoginStaff, layout: 'public' },
  { path: /^\/logout$/, component: Logout, layout: 'public' },

  { path: /^\/student$/, component: StudentDashboard, layout: 'student' },
  { path: /^\/student\/study-plan$/, component: StudentStudyPlan, layout: 'student' },
  { path: /^\/student\/requests$/, component: StudentRequests, layout: 'student' },
  { path: /^\/student\/requests\/new$/, component: StudentRequestNew, layout: 'student' },
  { path: /^\/student\/requests\/(.+)$/, component: StudentRequestDetail, layout: 'student', params: ['id'] },
  { path: /^\/student\/surveys$/, component: StudentSurveys, layout: 'student' },
  { path: /^\/student\/forms$/, component: StudentForms, layout: 'student' },
  { path: /^\/student\/profile$/, component: StudentProfile, layout: 'student' },
  { path: /^\/student\/degree$/, component: StudentDegree, layout: 'student' },
  { path: /^\/student\/contact$/, component: StudentContact, layout: 'student' },

  { path: /^\/staff$/, component: StaffDashboard, layout: 'staff' },
  { path: /^\/staff\/requests$/, component: StaffRequests, layout: 'staff' },
  { path: /^\/staff\/requests\/(.+)$/, component: StaffRequestDetail, layout: 'staff', params: ['id'] },
  { path: /^\/staff\/students$/, component: StaffStudents, layout: 'staff' },
  { path: /^\/staff\/students\/new$/, component: StaffStudentNew, layout: 'staff' },
  { path: /^\/staff\/students\/(.+)\/plan$/, component: StaffStudentPlanEdit, layout: 'staff', params: ['id'] },
  { path: /^\/staff\/students\/(.+)\/delete$/, component: StaffStudentDelete, layout: 'staff', params: ['id'] },
  { path: /^\/staff\/students\/(.+)\/edit$/, component: StaffStudentEdit, layout: 'staff', params: ['id'] },
  { path: /^\/staff\/students\/(.+)$/, component: StaffStudentDetail, layout: 'staff', params: ['id'] },
  { path: /^\/staff\/admins$/, component: StaffAdmins, layout: 'staff' },
  { path: /^\/staff\/admins\/(.+)$/, component: StaffAdminDetail, layout: 'staff', params: ['id'] },
  { path: /^\/staff\/surveys$/, component: StaffSurveys, layout: 'staff' },
  { path: /^\/staff\/surveys\/new$/, component: StaffSurveyNew, layout: 'staff' },
  { path: /^\/staff\/surveys\/(.+)$/, component: StaffSurveyDetail, layout: 'staff', params: ['id'] },
  { path: /^\/staff\/pages$/, component: StaffPages, layout: 'staff' },
  { path: /^\/staff\/settings$/, component: StaffSettings, layout: 'staff' },
  { path: /^\/staff\/emails$/, component: StaffEmails, layout: 'staff' },
  { path: /^\/staff\/emails\/(.+)$/, component: StaffEmailDetail, layout: 'staff', params: ['id'] },
  { path: /^\/staff\/logs$/, component: StaffLogs, layout: 'staff' },
  { path: /^\/staff\/course-list$/, component: StaffCourseList, layout: 'staff' },
  { path: /^\/staff\/analytics$/, component: StaffAnalytics, layout: 'staff' }
];

export const RouteContext = React.createContext({
  path: '/',
  navigate: () => {}
});

export function Router() {
  const [path, setPath] = React.useState(window.location.pathname);

  React.useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = React.useCallback((to) => {
    const pathname = to.split('?')[0].split('#')[0];
    if (pathname === path) return;
    window.history.pushState({}, '', to);
    setPath(pathname);
  }, [path]);

  const match = matchRoute(path);
  const Component = match.component;

  return (
    <RouteContext.Provider value={{ path, navigate }}>
      <Layout variant={match.layout}>
        <Component {...match.props} params={match.params} />
      </Layout>
    </RouteContext.Provider>
  );
}

export function Link({ to, children, className, ...rest }) {
  const { navigate } = React.useContext(RouteContext);
  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

function matchRoute(pathname) {
  for (const route of routes) {
    const match = pathname.match(route.path);
    if (match) {
      const params = {};
      if (route.params) {
        route.params.forEach((name, idx) => {
          params[name] = match[idx + 1];
        });
      }
      return {
        component: route.component,
        layout: route.layout,
        params,
        props: route.props || {}
      };
    }
  }
  return { component: NotFound, layout: 'public', params: {}, props: {} };
}
